import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportTypeSelector = ({ selectedType, onTypeSelect }) => {
  const reportTypes = [
    {
      id: 'eu-ets',
      title: 'EU ETS Surrender Report',
      description: 'Annual CO₂ emissions report for EU ETS compliance with verified emissions data and EUA surrender requirements',
      icon: 'FileText',
      deadline: 'Due: April 30, 2025',
      status: 'pending',
      requirements: ['Verified emissions data', 'EUA transaction records', 'Monitoring plan compliance'],
      estimatedTime: '15-20 minutes'
    },
    {
      id: 'fueleu',
      title: 'FuelEU Maritime Statement',
      description: 'Compliance statement for FuelEU Maritime regulation with GHG intensity calculations and penalty assessments',
      icon: 'Fuel',
      deadline: 'Due: March 31, 2025',
      status: 'draft',
      requirements: ['Fuel consumption data', 'GHG intensity calculations', 'Port call records'],
      estimatedTime: '10-15 minutes'
    },
    {
      id: 'imo-dcs',
      title: 'IMO DCS Report',
      description: 'Data Collection System report for IMO with fuel consumption, CO₂ emissions, and operational data',
      icon: 'Globe',
      deadline: 'Due: February 28, 2025',
      status: 'completed',
      requirements: ['Fuel oil consumption', 'CO₂ emissions', 'Distance travelled', 'Hours underway'],
      estimatedTime: '20-25 minutes'
    },
    {
      id: 'cii-evidence',
      title: 'CII Evidence Pack',
      description: 'Carbon Intensity Indicator evidence documentation with rating calculations and improvement measures',
      icon: 'Award',
      deadline: 'Due: May 31, 2025',
      status: 'pending',
      requirements: ['Operational data', 'CII calculations', 'Rating documentation', 'SEEMP Part III'],
      estimatedTime: '25-30 minutes'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'draft': return 'text-warning bg-warning/10';
      case 'pending': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'draft': return 'Edit';
      case 'pending': return 'Clock';
      default: return 'Circle';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Select Report Type</h2>
        <div className="text-sm text-muted-foreground">
          Choose the regulatory report you need to generate
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {reportTypes?.map((type) => (
          <div
            key={type?.id}
            className={`maritime-card p-6 cursor-pointer transition-maritime hover:shadow-maritime-md ${
              selectedType === type?.id ? 'ring-2 ring-primary bg-primary/5' : ''
            }`}
            onClick={() => onTypeSelect(type?.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedType === type?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <Icon name={type?.icon} size={20} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{type?.title}</h3>
                  <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(type?.status)}`}>
                    <Icon name={getStatusIcon(type?.status)} size={12} strokeWidth={2} />
                    <span className="capitalize">{type?.status}</span>
                  </div>
                </div>
              </div>
              {selectedType === type?.id && (
                <Icon name="Check" size={20} strokeWidth={2} className="text-primary" />
              )}
            </div>

            <p className="text-sm text-muted-foreground mb-4">{type?.description}</p>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Deadline:</span>
                <span className="font-medium text-foreground">{type?.deadline}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Est. Time:</span>
                <span className="font-medium text-foreground">{type?.estimatedTime}</span>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-2">Requirements:</div>
                <div className="space-y-1">
                  {type?.requirements?.map((req, index) => (
                    <div key={index} className="flex items-center space-x-2 text-xs">
                      <Icon name="CheckCircle" size={12} strokeWidth={2} className="text-success" />
                      <span className="text-muted-foreground">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedType && (
        <div className="flex items-center justify-between p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-center space-x-3">
            <Icon name="Info" size={16} strokeWidth={2} className="text-accent" />
            <span className="text-sm text-foreground">
              Selected: {reportTypes?.find(t => t?.id === selectedType)?.title}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => onTypeSelect(null)}>
            Change Selection
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportTypeSelector;